import { useRoomBorrowingStore } from "@/store/room_borrowing/roomBorrowingStore";
import { useState, useEffect, useCallback } from "react";
import { useRoomBorrowingNext } from "../RoomBorrowing";
import { IRoom } from "@/models/room/Room";
import LabSelect from "../../components/laboratory/LabSelect";
import RoomSelect from "../../components/rooms/RoomSelect";
import { IRoomType } from "@/models/room/RoomType";
import { Option } from "@/components/multi-select-filter/SelectionFilter";
import RoomTypeService from "@/lib/services/room/RoomTypeService";
import RoomService from "@/lib/services/room/RoomService";
import { useToast } from "@/lib/hooks/useToast";
import { UserFacingError } from "@/models/helper/UserFacingError";

const ChooseRooms = () => {
  const { toast } = useToast();
  const [rooms, setRooms] = useState<IRoom[]>([]);
  const [selectedType, setSelectedType] = useState<number | null>(null);
  const [roomTypes, setRoomTypes] = useState<IRoomType[]>([]);
  const [loading, setLoading] = useState(false);
  const store = useRoomBorrowingStore();
  const { setRoomBorrowingNext } = useRoomBorrowingNext();

  const [selectedSoftware, setSelectedSoftware] = useState<Option[]>([]);

  const handleSoftwareChange = (selected: Option[]) => {
    setSelectedSoftware(selected);
  };

  useEffect(() => {
    const selectedRoom = store.data.room;
    if (selectedRoom !== undefined && selectedRoom > 0) {
      setRoomBorrowingNext(true);
    } else {
      setRoomBorrowingNext(false);
    }
  }, [store.data.room, setRoomBorrowingNext]);

  const onSelect = (room: IRoom) => {
    store.update("room", room.id);
    store.update("room_type", room.type?.id);
    store.update("room_code", room.room_code);
    store.update("room_privacy_category", room.room_privacy_category);
    store.update("capacity", room.capacity);

    if (room.room_privacy_category === "Private") {
      store.update("is_private", true);
    } else {
      store.update("is_private", false);
    }
  };

  const onSelectType = (id: number) => {
    store.update("room_type", id);
    setSelectedType(id);
  };

  const fetchRoomsData = useCallback(
    async (softwares: Option[], ignore: boolean) => {
      setLoading(true);

      try {
        console.log("params", {
          room_location_id: store.data.location,
          room_type_id:
            !selectedType || selectedType === -1 ? undefined : selectedType,
          softwareIds: softwares.map((s) => s.id),
        });
        const rooms = await RoomService.getBorrowingRooms(
          store.data.location,
          !selectedType || selectedType === -1 ? undefined : selectedType,
          softwares.map((s) => s.id)
        );

        if (ignore === true) return;
        setRooms(rooms);
      } catch (error) {
        if (error instanceof UserFacingError) {
          toast({ title: error.title, description: error.description });
        }
      }
      setLoading(false);
    },
    [selectedType, store.data.location, toast]
  );

  useEffect(() => {
    if (store.data.location === -1) return;
    let ignore = false;
    fetchRoomsData(selectedSoftware, ignore);
    return () => {
      ignore = true;
    };
  }, [store.data.location, selectedType, fetchRoomsData]);

  useEffect(() => {
    let ignore: boolean = false;
    if (store.data.location < 0) return;
    const fetchData = async () => {
      const roomTypes = await RoomTypeService.getBorrowingRoomType(
        store.data.location.toString()
      );
      if (ignore === true) return;
      setRoomTypes(roomTypes);
    };
    fetchData();
    return () => {
      ignore = true;
    };
  }, [store.data.location]);

  const onSubmit = () => {
    fetchRoomsData(selectedSoftware, false);
  };

  return (
    <RoomSelect
      data={rooms}
      loading={loading}
      onSelect={onSelect}
      selected={store.data.room}
      selectedSoftware={selectedSoftware}
      onSoftwareChange={handleSoftwareChange}
      onSubmit={onSubmit}
    >
      <LabSelect
        data={roomTypes}
        hideAll={false}
        selected={selectedType}
        onSelect={onSelectType}
      />
    </RoomSelect>
  );
};

export default ChooseRooms;
